import type { Scale } from '@tunasong/schemas'
import type { Chord, ChordType, ChordVariant, TriadType } from '../chord/index.js'
import { isChordVariant } from '../chord/index.js'
import { getNotes as getChordNotes } from '../chord/get-notes.js'
import { getNoteChromaticDistance, getNoteChromaticIndex } from '../note/note-lib.js'
import { ScaleLib } from '../scale/index.js'
import type { ChordFunction } from './types.js'

/**
 * Map a plain Roman numeral to its flat-prefixed `ChordFunction` form.
 *
 * Why a table: only specific scale degrees have a defined flat-degree
 * counterpart in `ChordFunction`. ♭IV is exotic and unsupported.
 *
 * Both uppercase (major-quality) and lowercase (minor-quality) keys are
 * present because `prettyScaleChordDegree` lowercases the Roman numeral when
 * the chord built on that degree is minor or diminished — e.g. Locrian's
 * chord on the flat-3rd is D minor, which produces pretty='iii' and needs
 * to be flat-corrected to '♭iii'. Likewise Phrygian's chord on the flat-7th
 * is a minor triad, producing pretty='vii' that becomes '♭vii'.
 *
 * Uppercase 'V' → '♭V' supports Locrian's major triad on flat-5 (e.g. F
 * major in B Locrian). There is no lowercase '♭v' label because no mode in
 * v1.5's vocabulary produces a minor-quality chord on the flat-5th.
 *
 * The `'III+'` key supports harmonic minor's augmented chord on flat-3
 * (C augmented in A harmonic minor: C-E-G#). `prettyScaleChordDegree` emits
 * uppercase-with-plus for an augmented triad, then this table flattens it
 * because harmonic minor's intervals[2] is `'bIII'`.
 */
const FLAT_DEGREE_LABELS: Record<string, ChordFunction> = {
  II: '♭II',
  III: '♭III',
  V: '♭V',
  VI: '♭VI',
  VII: '♭VII',
  iii: '♭iii',
  vii: '♭vii',
  'III+': '♭III+',
}

/**
 * Map a plain Roman numeral to its sharp-prefixed `ChordFunction` form.
 *
 * Mirror of `FLAT_DEGREE_LABELS` for the `#` prefix. Single entry for v1.5:
 * Lydian's raised-4 diminished triad (F#° in C Lydian: F#-A-C). This is the
 * Lydian-characteristic chord — the raised 4 defines Lydian, so the chord
 * built on it deserves a label that calls out the alteration.
 *
 * The two tables are disjoint at v1.5 scope: no degree that's flatted in one
 * mode is sharpened in another within the supported scale set, so the
 * type-guard can consult SHARP first then FLAT without overlap concerns.
 */
const SHARP_DEGREE_LABELS: Record<string, ChordFunction> = {
  'iv°': '#iv°',
}

/**
 * Position in the `intervals` array → uppercase Roman numeral. Indices match
 * the convention in `lib/music-lib/src/scale/scale.ts` where intervals[0]=I,
 * intervals[1]=II, … intervals[6]=VII.
 */
const INTERVAL_INDEX_TO_DEGREE = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'] as const

/**
 * Returns true when the scale's natural form of `degree` is flatted relative
 * to the major scale (e.g., Dorian's III is `bIII`, Mixolydian's VII is
 * `bVII`). Reads the scale spec's `intervals` array directly — no semitone
 * arithmetic, no mode-name list to maintain. Any future minor-flavored scale
 * (or harmonic/melodic-minor variant) added to `SCALES` becomes correct
 * automatically as long as its intervals follow the existing `'bX'` convention.
 */
const scaleHasFlatDegree = (scale: Scale, degree: 'II' | 'III' | 'V' | 'VI' | 'VII'): boolean => {
  const { intervals } = ScaleLib.getScaleSpec(scale)
  const index = INTERVAL_INDEX_TO_DEGREE.indexOf(degree)
  return intervals[index] === `b${degree}`
}

/**
 * Returns true when the scale's natural form of `degree` is sharpened relative
 * to the major scale (e.g., Lydian's IV is `#IV`). Parallel to
 * `scaleHasFlatDegree`. At v1.5 scope only Lydian's IV qualifies.
 */
const scaleHasSharpDegree = (scale: Scale, degree: 'IV'): boolean => {
  const { intervals } = ScaleLib.getScaleSpec(scale)
  const index = INTERVAL_INDEX_TO_DEGREE.indexOf(degree)
  return intervals[index] === `#${degree}`
}

/**
 * Map a `ChordVariant.variant` to its underlying triad variant for diatonic
 * lookup. Returns `null` for chords without a triadic third (sus, power),
 * which forces the analyzer to fall through (borrowing → secondary → chromatic).
 *
 * The codebase only produces a finite set of `variant` strings — see
 * `lib/music-lib/src/chord/all-chords.ts` (triad × element composition), the
 * parser in `chord-regexp.ts`, and the `ChordType` union in
 * `chord-types.ts`. This table is exhaustive against the parser-produced set
 * and defensively maps the remaining `ChordType` values for code paths that
 * construct chords directly (AI suggestions, future parser widening).
 *
 * Half-diminished (`m7b5`, `min7b5`, `ø`, `ø7`) reduces to **diminished**, not
 * minor — the underlying triad has a ♭5. This is the most common implementation
 * mistake; the test suite includes regression coverage for `Bm7b5 → vii°`.
 *
 * Sus chords (`sus2`, `sus4`, `7sus4`, `9sus4`, `maj7sus4`, …) return `null`:
 * they have no third, so they're not diatonic triads of any major or minor
 * key. `analyzeChord` labels them `'open'` when every tone is in the scale
 * (see `isOpenChordInScale`) and `'chromatic'` otherwise.
 *
 * Why a table and not regex-stripping: variant strings are an irregular grammar
 * (`m7b5`, `7#5`, `madd9`, `7sus4`, `maj7#11`) and substring rules misclassify
 * edge cases (e.g. `'maj7'` contains `'7'` but is NOT a dominant seven).
 */
const TRIAD_REDUCTION: Record<string, TriadType | null> = {
  // Plain triads — already a triad. 'M' is the parser's alias for the empty
  // major-triad variant; we map it to '' so downstream sees the canonical form.
  '': '',
  M: '',
  m: 'm',
  aug: 'aug',
  dim: 'dim',

  // Plain 7th chords reduce to their underlying triad quality. The dropped
  // seventh is checked against the scale in `getDiatonicFunction` (see
  // `SEVENTH_SEMITONES`).
  '7': '',
  maj7: '',
  m7: 'm',
  mmaj7: 'm',

  // Half-diminished (m7b5) — underlying triad is DIMINISHED. Aliases `min7b5`,
  // `ø`, `ø7` all map the same way.
  m7b5: 'dim',
  min7b5: 'dim',

  // Augmented-7 family. `7#5` is the parser's canonical form; `aug7`, `+7`
  // are aliases. Underlying triad is augmented.
  '7#5': 'aug',
  aug7: 'aug',

  // Dominant-7 alterations. The underlying triad is major; the seventh check
  // in `getDiatonicFunction` rejects them where the ♭7 is outside the scale.
  '7b5': '',
  '7#9': '',
  '7b9': '',
  '7#11': '',
  '7b13': '',

  // Diminished and augmented 7ths. Defensive: not produced by the current
  // parser but legal in `ChordType` for direct construction.
  dim7: 'dim',
  augmaj7: 'aug',
  dimmaj7: 'dim',

  // 6 / 9 / add families. The third determines the underlying triad; the
  // extension (6, 9, add9, add11, …) is informational.
  '6': '',
  m6: 'm',
  '9': '',
  m9: 'm',
  add9: '',
  madd9: 'm',
  add11: '',
  madd11: 'm',

  // Major extended chords (maj7 with an alteration) — same major-triad core.
  'maj7#11': '',

  // Sus / power: no triadic third — treat as non-diatonic for v1.
  '5': null,
  sus2: null,
  sus4: null,
  sus2sus4: null,
  '7sus2': null,
  '7sus4': null,
  '7sus2sus4': null,
  '9sus4': null,
  '7b5sus4': null,
  maj7sus4: null,
}

/**
 * Reduce a chord to its triad. For ChordVariant inputs, look up the variant
 * and return a new chord with only the triad variant. For ChordNotes (notes-
 * driven chords) we cannot safely reduce — return the original; downstream
 * `getChordDegree` already short-circuits on ChordNotes.
 *
 * Returns `null` when the variant has no triadic third (sus, power) so the
 * analyzer can fall through.
 */
const reduceToTriad = (chord: Chord): Chord | null => {
  if (!isChordVariant(chord)) {
    return chord
  }
  const triadVariant = TRIAD_REDUCTION[chord.variant]
  if (triadVariant === undefined) {
    // Unknown variant — be conservative and pass through unmodified. If it
    // happens to be a triad we'll match; otherwise we'll return null and the
    // analyzer falls through to chromatic.
    return chord
  }
  if (triadVariant === null) {
    return null
  }
  return { ...chord, variant: triadVariant as ChordType } satisfies ChordVariant
}

/**
 * Dominant-7 variants (plain and altered). Blues style opts these out of the
 * seventh-in-scale check (`allowDominantSevenths`), so `C7` in C blues reads
 * `I` even though its ♭7 is outside C major.
 */
const DOMINANT_7_VARIANTS: ReadonlySet<string> = new Set([
  '7',
  // Dominant ninth: same ♭7, so C9 in C blues reads I like C7. The parser
  // emits no plain `11` or `13` (only with an alteration, e.g. `13b9`, which
  // `TRIAD_REDUCTION` does not know), so they are not listed.
  '9',
  '7#5',
  '7b5',
  '7#9',
  '7b9',
  '7#11',
  '7b13',
  // `aug7` / `+7` are aliases for `7#5` but parser-produced variants can be
  // either form. List both for safety.
  'aug7',
])

/**
 * Semitones from the root to the seventh, for every variant whose seventh
 * `TRIAD_REDUCTION` drops. Altered dominants list the plain ♭7: the check
 * covers the four seventh-chord tones, not the alterations.
 */
const SEVENTH_SEMITONES: Record<string, number> = {
  '7': 10,
  maj7: 11,
  m7: 10,
  mmaj7: 11,
  m7b5: 10,
  min7b5: 10,
  '7#5': 10,
  aug7: 10,
  '7b5': 10,
  '7#9': 10,
  '7b9': 10,
  '7#11': 10,
  '7b13': 10,
  // Sus sevenths. `reduceToTriad` returns null for these before the seventh
  // check runs, so today they only document intent; `isOpenChordInScale`
  // already rejects a sus chord whose seventh is outside the scale.
  '7sus2': 10,
  '7sus4': 10,
  '7sus2sus4': 10,
  '9sus4': 10,
  '7b5sus4': 10,
  maj7sus4: 11,
  dim7: 9,
  augmaj7: 11,
  dimmaj7: 11,
  '9': 10,
  m9: 10,
  'maj7#11': 11,
}

/**
 * True when the chord has no seventh, or its seventh is in `scale` (Bowie,
 * 2026-10-10, GH #2136). A seventh outside the key makes the chord
 * non-diatonic: Dmaj7 in E minor (C♯) is not ♭VII, while D7 (C♮) is.
 *
 * Exception: the natural-minor KEY's raised 7th on the tonic, so i(maj7)
 * (Em(maj7) in E minor) stays `i` — the same harmonic-minor lift as V.
 */
const isSeventhInScale = (chord: Chord, scale: Scale): boolean => {
  if (!isChordVariant(chord)) {
    return true
  }
  const semitones = SEVENTH_SEMITONES[chord.variant]
  if (semitones === undefined) {
    return true
  }
  const seventh = (getNoteChromaticIndex(chord.root) + semitones) % 12
  if (ScaleLib.getNotesChromaticIndices(scale).includes(seventh)) {
    return true
  }
  return scale.type === 'minor' && semitones === 11 && getNoteChromaticDistance(scale.root, chord.root) === 0
}

/**
 * True when the chord is a dominant-7 variant in the carved-out set
 * (`'7'`, `'7#5'`, `'7b5'`, `'7#9'`, `'7b9'`). Exposed for callers that
 * need to apply style-specific descriptions to dom7 chords (e.g. the blues
 * branch in `analyzeChord` swaps `I` / `IV` descriptions for dom7 chords).
 */
export const isDominantSeventhVariant = (chord: Chord): boolean =>
  isChordVariant(chord) && DOMINANT_7_VARIANTS.has(chord.variant)

/**
 * Variants that read as "major-quality V chord" for the harmonic-minor-dominant
 * fallback. Plain major triad (`''` / `'M'`), dominant-7 family, and major-6/
 * 9/add extensions all qualify because their underlying triad is major.
 *
 * Excluded: minor (`'m'`, `'m7'`, …) because writing G minor in C minor is the
 * natural-minor v and must NOT promote to V; sus (no third — function
 * unresolved); diminished and augmented (different qualities entirely).
 *
 * Was previously duplicated in `cadence.ts` as `MAJOR_OR_DOMINANT_VARIANTS`
 * for the `isMajorVInMinorKey` cadence-side workaround; that duplicate was
 * removed in the same patch that introduced this set — cadence now relies on
 * `getDiatonicFunction` returning `'V'` directly for major V in minor.
 */
const MAJOR_OR_DOMINANT_VARIANTS: ReadonlySet<string> = new Set([
  '',
  'M',
  '7',
  'maj7',
  '6',
  '9',
  'add9',
  '7b5',
  '7#5',
  '7#9',
  '7b9',
  '7#11',
  '7b13',
  'aug7',
  'maj7#11',
])

/**
 * Harmonic-minor-dominant fallback. True when:
 *   1. The scale is the natural-minor KEY (`scale.type === 'minor'`),
 *      NOT Aeolian-as-mode, NOT Dorian / Mixolydian / Phrygian / Locrian.
 *      The gate intentionally mirrors `cadence.ts`'s `hasNoLeadingTone`
 *      exemption — keys imply harmonic-minor lift, modes imply purity of
 *      the natural intervals (so a major V in Mixolydian is mode-mixture,
 *      NOT a dominant resolution and must not be labeled V).
 *   2. The chord's root is exactly a perfect-5 (7 semitones) above the
 *      scale tonic.
 *   3. The chord quality is major or dominant-7 (any of the
 *      `MAJOR_OR_DOMINANT_VARIANTS`). A minor chord on the V degree is the
 *      natural-minor `v` and must NOT promote to V — chord quality drives
 *      the decision.
 *
 * Why a fallback rather than fixing `chordTypes['minor'].V`: the natural-minor
 * scale's diatonic 5-chord IS minor (Em in A minor, Gm in C minor). The
 * harmonic-minor raised-7 is a USAGE convention, not a property of the natural
 * minor scale itself. Changing `chordTypes` would mislabel any user who writes
 * Gm intending the natural-minor v.
 *
 * Note that harmonic minor (`scale.type === 'harmonicminor'`) and melodic
 * minor (`'melodicminor'`) are intentionally EXCLUDED from this fallback —
 * both already declare `chordTypes[V] === ''` (major) so a major V chord
 * resolves to V via the normal diatonic path with no fallback needed.
 */
const isMajorVInMinorKey = (chord: Chord, scale: Scale): boolean => {
  if (scale.type !== 'minor') {
    return false
  }
  if (!isChordVariant(chord)) {
    return false
  }
  if (!MAJOR_OR_DOMINANT_VARIANTS.has(chord.variant)) {
    return false
  }
  // A perfect 5th above the tonic is 7 semitones (modulo 12).
  return getNoteChromaticDistance(scale.root, chord.root) === 7
}

/**
 * Leading-tone diminished chords that the natural-minor KEY lifts to `vii°`:
 * the diminished triad and the fully-diminished seventh. Both are diatonic to
 * harmonic minor, the same raised-7 usage convention that lifts a major V
 * (see `isMajorVInMinorKey`).
 *
 * The half-diminished (`m7b5` and aliases) is excluded on purpose (Bowie,
 * 2026-10-10): on the raised 7th its seventh is the RAISED 6th (C# in E
 * minor), which is melodic-minor color, not the harmonic-minor dominant
 * function. It keeps its parallel-mode reading.
 */
const LEADING_TONE_DIMINISHED_VARIANTS: ReadonlySet<string> = new Set(['dim', 'dim7'])

/**
 * Harmonic-minor leading-tone fallback, the companion of `isMajorVInMinorKey`.
 * True when the scale is the natural-minor KEY (not Aeolian-as-mode), the
 * chord root is a major 7th (11 semitones) above the tonic, and the chord is
 * a diminished triad or diminished seventh.
 */
const isLeadingToneDiminishedInMinorKey = (chord: Chord, scale: Scale): boolean =>
  scale.type === 'minor' &&
  isChordVariant(chord) &&
  LEADING_TONE_DIMINISHED_VARIANTS.has(chord.variant) &&
  getNoteChromaticDistance(scale.root, chord.root) === 11

/**
 * True for a chord with no triadic third (sus, power) whose every tone is in
 * `scale`. Such chords have no degree numeral (their quality is unresolved),
 * but they are plainly in the key: the analyzer labels them `'open'`.
 *
 * Chords whose notes cannot be resolved return false, so they fall through to
 * `'chromatic'` rather than reading as in-key by an empty-set accident.
 */
export const isOpenChordInScale = (chord: Chord, scale: Scale): boolean => {
  if (!isChordVariant(chord) || TRIAD_REDUCTION[chord.variant] !== null) {
    return false
  }
  const chordNotes = getChordNotes(chord)
  if (chordNotes.length === 0) {
    return false
  }
  const scaleIndices = new Set(ScaleLib.getNotesChromaticIndices(scale))
  return chordNotes.every(note => scaleIndices.has(getNoteChromaticIndex(note)))
}

export interface GetDiatonicFunctionOptions {
  /**
   * When `true`, dom7 chords skip the seventh-in-scale check — a dom7 chord
   * matches its diatonic degree directly without falling through to the
   * secondary-dominant analyzer. Set by `analyzeChord` when the caller signals
   * `context.style === 'blues'`, where I7 / IV7 / V7 are the primary chords
   * (not secondary dominants).
   *
   * Default: `false` (common-practice tonality, the v1 behavior).
   */
  allowDominantSevenths?: boolean
}

/**
 * If `chord` is a diatonic chord of `scale`, return its functional label
 * (e.g., 'I', 'ii', 'V', 'vii°'). Otherwise return null.
 *
 * Reduces 7th/9th/6th/add chords to their underlying triad before lookup —
 * `ScaleLib.getChordDegree` requires an exact triad match (see scale.ts:302).
 * Without reduction, `Cmaj7` in C major would miss `I` and the analyzer would
 * fall all the way through to `chromatic`.
 *
 * Seventh chords are diatonic only when the seventh is in the scale too (see
 * `isSeventhInScale`): `G7` in C major is V, `D7` in E minor is ♭VII, but
 * `C7` in C major returns null so the analyzer can read it as `V/IV`.
 * Callers passing `allowDominantSevenths: true` (blues style) skip the check
 * for dominant 7ths — `C7` in C blues matches `I` directly.
 *
 * For flatted scale degrees (♭II in Phrygian/Locrian, ♭III in any minor-third
 * mode, ♭VI in Aeolian/Phrygian/Locrian/harmonic-minor, ♭VII in Dorian/
 * Mixolydian/Aeolian/Phrygian), `prettyScaleChordDegree` returns plain Roman
 * numerals — apply the flat marker here. We read the scale spec's `intervals`
 * array directly so any minor-flavored mode (not just `scale.type === 'minor'`)
 * is handled. See `scaleHasFlatDegree`.
 */
export const getDiatonicFunction = (
  chord: Chord,
  scale: Scale,
  options: GetDiatonicFunctionOptions = {}
): ChordFunction | null => {
  const triad = reduceToTriad(chord)
  if (!triad) {
    return null
  }
  const degree = ScaleLib.getChordDegree(scale, triad)
  if (!degree) {
    // Harmonic-minor-dominant fallback: in the natural-minor KEY a major V
    // chord (raised leading tone) is the common-practice dominant, but
    // natural-minor's diatonic 5-chord is minor — so `getChordDegree` rejects
    // the major triad on V. Recognise it here and return 'V' directly. See
    // `isMajorVInMinorKey` for the full gating contract (mode-only, root must
    // sit a P5 above tonic, chord must be major-or-dominant7).
    //
    // This intentionally supersedes `cadence.ts`'s `isMajorVInMinorKey`
    // workaround — once this lands, the cadence-side helper and its
    // `MAJOR_OR_DOMINANT_VARIANTS` set can be deleted in favor of reading the
    // diatonic-function output directly.
    if (isMajorVInMinorKey(chord, scale)) {
      return 'V'
    }
    // Same lift for the leading-tone diminished triad and dim7 (vii° in
    // harmonic minor). See `isLeadingToneDiminishedInMinorKey`.
    if (isLeadingToneDiminishedInMinorKey(chord, scale)) {
      return 'vii°'
    }
    return null
  }
  // A seventh outside the scale makes the chord non-diatonic, so the
  // borrowing / secondary-dominant analyzers get a turn (`C7` in C major →
  // `V/IV`). Blues style keeps its I7 / IV7 on the degree.
  const isBluesDominant = options.allowDominantSevenths && isDominantSeventhVariant(chord)
  if (!isBluesDominant && !isSeventhInScale(chord, scale)) {
    return null
  }
  const pretty = ScaleLib.prettyScaleChordDegree(scale, degree)
  // Sharp-degree correction (Lydian's #iv° characteristic chord). Consult
  // SHARP_DEGREE_LABELS first because the two tables are disjoint at v1.5
  // scope — no degree is both sharp in one mode and flat in another within
  // the supported scale set.
  const sharpLabel = SHARP_DEGREE_LABELS[pretty]
  if (sharpLabel) {
    // pretty is currently only 'iv°' (Lydian's diminished raised-4). Strip
    // the `°` and uppercase to get the bare degree label for the spec lookup.
    const uppercaseDegree = pretty.replace('°', '').toUpperCase()
    if (uppercaseDegree === 'IV' && scaleHasSharpDegree(scale, uppercaseDegree)) {
      return sharpLabel
    }
  }
  const flatLabel = FLAT_DEGREE_LABELS[pretty]
  if (flatLabel) {
    // Map lowercase pretty (minor-quality chord on the degree) back to the
    // uppercase scale degree so `scaleHasFlatDegree` can read the interval.
    // Both Phrygian (chord on VII = minor → pretty 'vii') and Locrian (chord
    // on III = minor → pretty 'iii') need this path; their underlying
    // intervals are 'bVII' / 'bIII' respectively.
    //
    // Augmented form 'III+' (harmonic minor's flat-3 augmented) needs the `+`
    // stripped before the uppercase normalization so the degree-label lookup
    // matches.
    const uppercaseDegree = pretty.replace('+', '').toUpperCase()
    if (
      uppercaseDegree === 'II' ||
      uppercaseDegree === 'III' ||
      uppercaseDegree === 'V' ||
      uppercaseDegree === 'VI' ||
      uppercaseDegree === 'VII'
    ) {
      if (scaleHasFlatDegree(scale, uppercaseDegree)) {
        return flatLabel
      }
    }
  }
  return pretty as ChordFunction
}
